import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { apiGet, apiPost } from '../services/api';

export type LeaveRequestType = 'normal' | 'sick';
export type LeaveRequestStatus = 'pending' | 'approved' | 'rejected';

export interface User {
  id: number;
  name: string;
  email: string;
}

export interface LeaveRequest {
  id: number;
  user_id: number;
  type: LeaveRequestType;
  start_date: string;
  end_date: string;
  days: number;
  note: string | null;
  status: LeaveRequestStatus;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  user?: User;
  approved_by?: User | null;
  rejected_by?: User | null;
}

export interface CreateLeaveRequestPayload {
  type: LeaveRequestType;
  start_date: string;
  end_date: string;
  note?: string;
}

export async function getMany({
  paginationModel,
  filterModel,
  sortModel,
  status,
  date,
  startDateFrom,
  startDateTo,
  type,
  sortBy,
  sortOrder,
  id,
}: {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  status?: LeaveRequestStatus;
  date?: string;
  startDateFrom?: string;
  startDateTo?: string;
  type?: LeaveRequestType;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  id?: number;
}): Promise<{ items: LeaveRequest[]; itemCount: number }> {
  const page = paginationModel.page + 1; // API uses 1-based pagination
  const perPage = paginationModel.pageSize;

  // Build query parameters
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('per_page', String(perPage));

  // Always include sort parameters - default to created_at desc
  params.append('sort_by', sortBy || 'created_at');
  params.append('sort_order', sortOrder || 'desc');

  if (status) {
    params.append('status', status);
  }
  if (date) {
    params.append('date', date);
  }
  if (startDateFrom) {
    params.append('start_date_from', startDateFrom);
  }
  if (startDateTo) {
    params.append('start_date_to', startDateTo);
  }
  if (type) {
    params.append('type', type);
  }
  if (id) {
    params.append('id', String(id));
  }

  const response = await apiGet<{
    current_page: number;
    data: LeaveRequest[];
    total: number;
    per_page: number;
    last_page: number;
  }>(`/api/leave-requests?${params.toString()}`);

  const leaveRequests = response.data || [];
  const totalCount = response.total || 0;

  let filteredLeaveRequests = [...leaveRequests];

  // Apply filters
  if (filterModel?.items?.length) {
    filterModel.items.forEach(({ field, value, operator }) => {
      if (!field || value == null) {
        return;
      }

      filteredLeaveRequests = filteredLeaveRequests.filter((request) => {
        const requestValue = request[field as keyof LeaveRequest];

        switch (operator) {
          case 'contains':
            return String(requestValue).toLowerCase().includes(String(value).toLowerCase());
          case 'equals':
            return requestValue === value;
          case 'startsWith':
            return String(requestValue).toLowerCase().startsWith(String(value).toLowerCase());
          case 'endsWith':
            return String(requestValue).toLowerCase().endsWith(String(value).toLowerCase());
          case '>':
            return requestValue > value;
          case '<':
            return requestValue < value;
          default:
            return true;
        }
      });
    });
  }

  // Apply sorting
  if (sortModel?.length) {
    filteredLeaveRequests.sort((a, b) => {
      for (const { field, sort } of sortModel) {
        if (a[field as keyof LeaveRequest] < b[field as keyof LeaveRequest]) {
          return sort === 'asc' ? -1 : 1;
        }
        if (a[field as keyof LeaveRequest] > b[field as keyof LeaveRequest]) {
          return sort === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
  }

  return {
    items: filteredLeaveRequests,
    itemCount: totalCount,
  };
}

export async function getOne(leaveRequestId: number) {
  return apiGet<LeaveRequest>(`/api/leave-requests/${leaveRequestId}`);
}

export async function createOne(data: CreateLeaveRequestPayload) {
  return apiPost<LeaveRequest>('/api/leave-requests', data);
}

export async function approveLeaveRequest(leaveRequestId: number) {
  return apiPost<LeaveRequest>(`/api/leave-requests/${leaveRequestId}/approve`, {});
}

export async function rejectLeaveRequest(leaveRequestId: number) {
  return apiPost<LeaveRequest>(`/api/leave-requests/${leaveRequestId}/reject`, {});
}

